/**
 * Organization API service.
 * Calls backend organization routes (disable, etc.).
 *
 * Env: REACT_APP_API_URL, REACT_APP_API_AUTHORIZATION
 */

const getBaseUrl = () =>
  (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

/**
 * Disable an organization via the backend API.
 * Handles Twilio, Auth0, Zoho, and Firestore updates server-side.
 *
 * @param {Object} params
 * @param {string} params.zohoAccountId - Zoho CRM Account id (required)
 * @param {string} params.firebaseId - Firestore organization document id
 * @param {string} [params.twilioNumber] - Org prayer line number (E.164)
 * @returns {Promise<{ status: number, data: Object, partial: boolean }>}
 */
export async function disableOrganization({
  zohoAccountId,
  firebaseId,
  twilioNumber,
}) {
  const base = getBaseUrl();
  if (!base) {
    const err = new Error("REACT_APP_API_URL is not configured");
    err.status = 0;
    throw err;
  }

  const body = {
    zoho_account_id: zohoAccountId,
    firebase_id: firebaseId,
  };
  if (twilioNumber) {
    body.twilio_number = twilioNumber;
  }

  const res = await fetch(`${base}/organization/disable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(process.env.REACT_APP_API_AUTHORIZATION && {
        Authorization: process.env.REACT_APP_API_AUTHORIZATION,
      }),
    },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  if (res.status === 200 || res.status === 207) {
    return { status: res.status, data, partial: res.status === 207 };
  }

  const err = new Error(
    data.message || data.error || res.statusText || "Failed to disable organization",
  );
  err.status = res.status;
  err.response = data;
  throw err;
}
